import Cookies from "js-cookie";

const TOKEN_KEY = "TASIS:TOKEN";
const USER_KEY = "TASIS:USER";
const PERMISSIONS_KEY = "TASIS:PERMISSIONS";
const REMEMBER_KEY = "TASIS:REMEMBER";
const USERNAME_KEY = "TASIS:REMEMBERED_USERNAME";
const PASSWORD_KEY = "TASIS:REMEMBERED_PASSWORD";
const MAX_COOKIE_BYTES = 3500;

const SESSION_KEYS = [TOKEN_KEY, USER_KEY, PERMISSIONS_KEY];

const getCookieOptions = (remember) => {
  const options = {
    sameSite: "Lax",
    path: "/",
  };
  if (remember) {
    options.expires = 30;
  }
  return options;
};

const parseJSON = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const byteLength = (value) => new TextEncoder().encode(String(value)).length;

const setPreferCookie = (key, value, remember = true) => {
  const stringValue = typeof value === "string" ? value : JSON.stringify(value);

  if (byteLength(stringValue) <= MAX_COOKIE_BYTES) {
    Cookies.set(key, stringValue, getCookieOptions(remember));
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    return "cookie";
  }

  Cookies.remove(key, { path: "/" });
  if (remember) {
    localStorage.setItem(key, stringValue);
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.setItem(key, stringValue);
    localStorage.removeItem(key);
  }
  return remember ? "localStorage" : "sessionStorage";
};

const getPreferCookie = (key) => {
  const cookieValue = Cookies.get(key);
  if (cookieValue) return cookieValue;
  return localStorage.getItem(key) || sessionStorage.getItem(key);
};

const removeBoth = (key) => {
  Cookies.remove(key, { path: "/" });
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

export const authCookies = {
  getRememberMe() {
    return localStorage.getItem(REMEMBER_KEY) === "1";
  },

  setRememberMe(remember) {
    if (remember) {
      localStorage.setItem(REMEMBER_KEY, "1");
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  },

  getRememberedUsername() {
    return localStorage.getItem(USERNAME_KEY) || "";
  },

  setRememberedUsername(username) {
    const value = String(username || "").trim();
    if (value) {
      localStorage.setItem(USERNAME_KEY, value);
    } else {
      localStorage.removeItem(USERNAME_KEY);
    }
  },

  getRememberedPassword() {
    return localStorage.getItem(PASSWORD_KEY) || "";
  },

  setRememberedPassword(password) {
    if (password) {
      localStorage.setItem(PASSWORD_KEY, String(password));
    } else {
      localStorage.removeItem(PASSWORD_KEY);
    }
  },

  getToken() {
    return getPreferCookie(TOKEN_KEY);
  },

  setToken(token, remember = true) {
    if (!token) {
      removeBoth(TOKEN_KEY);
      return;
    }
    setPreferCookie(TOKEN_KEY, token, remember);
  },

  getUser() {
    return parseJSON(getPreferCookie(USER_KEY), {});
  },

  setUser(user, remember = true) {
    if (!user || !Object.keys(user).length) {
      removeBoth(USER_KEY);
      return;
    }
    setPreferCookie(USER_KEY, user, remember);
  },

  getPermissions() {
    const fromStore = parseJSON(getPreferCookie(PERMISSIONS_KEY), null);
    if (Array.isArray(fromStore)) return fromStore;

    const user = this.getUser();
    return Array.isArray(user?.permissions) ? user.permissions : [];
  },

  setPermissions(permissions = [], remember = true) {
    const list = Array.isArray(permissions) ? permissions : [];
    setPreferCookie(PERMISSIONS_KEY, list, remember);
  },

  setSession({ token, user, remember = false, username, password } = {}) {
    this.setRememberMe(remember);
    if (remember) {
      this.setRememberedUsername(
        username || user?.username || user?.email || ""
      );
      this.setRememberedPassword(password || "");
    } else {
      this.setRememberedUsername("");
      this.setRememberedPassword("");
    }
    this.setToken(token, remember);
    this.setUser(user, remember);
    this.setPermissions(user?.permissions || [], remember);
  },

  clearSession() {
    SESSION_KEYS.forEach(removeBoth);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  hasSession() {
    return Boolean(this.getToken() && Object.keys(this.getUser() || {}).length);
  },
};
